import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Video, GraduationCap, Building2, Wrench, Loader2 } from "lucide-react";
import axios from "axios";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  "video-gen": Video,
  "image-gen": Wrench,
  "learning": GraduationCap,
  "studios": Building2,
};

// Default resource data (fallback if no DB data)
const defaultCategories = [
  {
    id: "video-gen",
    title: "Video Generation",
    icon: "video-gen",
    items: [
      { name: "Veo 2", url: "https://deepmind.google/technologies/veo/" },
      { name: "Kling AI", url: "https://klingai.com/" },
      { name: "Runway ML", url: "https://runwayml.com/" },
      { name: "Pika Labs", url: "https://pika.art/" },
      { name: "Sora", url: "https://openai.com/sora" },
      { name: "Luma Dream Machine", url: "https://lumalabs.ai/dream-machine" },
      { name: "Haiper", url: "https://haiper.ai/" },
      { name: "Minimax", url: "https://hailuoai.video/" },
    ]
  },
  {
    id: "image-gen",
    title: "Image Generation",
    icon: "image-gen",
    items: [
      { name: "Midjourney", url: "https://midjourney.com/" },
      { name: "DALL-E 3", url: "https://openai.com/dall-e-3" },
      { name: "Stable Diffusion", url: "https://stability.ai/" },
      { name: "Leonardo AI", url: "https://leonardo.ai/" },
      { name: "Ideogram", url: "https://ideogram.ai/" },
      { name: "Flux", url: "https://blackforestlabs.ai/" },
    ]
  },
  {
    id: "learning",
    title: "Learning & Education",
    icon: "learning",
    items: [
      { name: "Curious Refuge", url: "https://www.curiousrefuge.com/" },
      { name: "AI Film Academy", url: "https://aifilmacademy.com/" },
      { name: "School of Motion", url: "https://www.schoolofmotion.com/" },
      { name: "Runway Academy", url: "https://runwayml.com/academy/" },
    ]
  },
  {
    id: "studios",
    title: "AI Film & Ad Studios",
    icon: "studios",
    items: [
      { name: "Waymark", url: "https://waymark.com/" },
      { name: "Synthesia", url: "https://www.synthesia.io/" },
      { name: "HeyGen", url: "https://www.heygen.com/" },
      { name: "Wonder Dynamics", url: "https://wonderdynamics.com/" },
      { name: "Flawless AI", url: "https://www.flawlessai.com/" },
      { name: "AI Ad Studio", url: "https://aiadstudio.com/" },
    ]
  },
];

export default function Resources() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resource-categories`);
      if (response.data && response.data.length > 0) {
        setCategories(response.data);
      } else {
        setCategories(defaultCategories);
      }
    } catch (error) {
      console.error("Error fetching resources:", error);
      setCategories(defaultCategories);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="AI Resources"
        description="Essential AI filmmaking tools and resources. Video generation, image creation, learning platforms, and studios."
        url="https://aifilmsociety.org/resources"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Tools & Resources
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            AI Resources
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Essential tools and platforms for AI filmmaking — from video generation 
            to learning resources and professional studios.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : (
          <>
            {/* Resource Categories - 3 Column Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category, categoryIndex) => {
                const Icon = iconMap[category.icon] || Video;
                return (
                  <motion.section
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: categoryIndex * 0.1 }}
                    className="scroll-mt-24"
                    id={category.id}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-purple/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-purple-light" />
                      </div>
                      <div className="min-w-0">
                        <h2 className="font-syne text-lg font-bold text-white truncate">
                          {category.title}
                        </h2>
                      </div>
                    </div>

                    <div className="bg-void-surface border border-white/5 divide-y divide-white/5">
                      {category.items && category.items.map((item, itemIndex) => (
                        <a
                          key={item.name || itemIndex}
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`resource-${category.id}-${itemIndex}`}
                          className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors group"
                        >
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-medium text-white group-hover:text-purple-light transition-colors truncate">
                              {item.name}
                            </h3>
                          </div>
                          <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-purple-light transition-colors flex-shrink-0 ml-2" />
                        </a>
                      ))}
                    </div>
                  </motion.section>
                );
              })}
            </div>

            {/* Suggest a Resource */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-12 text-center"
            >
              <p className="text-slate-500 text-sm">
                Know a great AI tool we should add?{" "}
                <a href="mailto:sam@aiadstudio.com" className="text-purple-light hover:text-white transition-colors">
                  Let us know
                </a>
              </p>
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
